import React from 'react';
import Link from 'next/link';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { GROQCTA } from '@/sanity/lib/definitions';
import { useCTAWrapper } from '@/hooks/useCTAWrapper';
import Anchor from '@/components/global/utils/Anchor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shadcn/ui/dialog';
import { cn } from '@/lib/utils';
import PopupContent from '@/components/global/PopupContent';

// import dots from "@/public/launch/dots2.png";
// import accessibility from "@/public/icons/Accessibility--alt.svg";

interface CTAWrapperProps {
  cta: GROQCTA | null | undefined;
  children: React.ReactNode;
  className?: string;
}

export function CTAWrapper({ cta, children, className }: CTAWrapperProps) {
  const { isLink, link, isAnchor, anchor, isPopup, popup, trackingAction } =
    useCTAWrapper(cta);

  if (isLink && link) {
    // Clone children and add data-track-action to the clickable element
    const enhancedChildren = React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement,
          {
            'data-track-action': trackingAction,
          } as any,
        )
      : children;

    return (
      <Link href={`${link}`} className={cn('inline-block', className)}>
        {enhancedChildren}
      </Link>
    );
  }

  if (isAnchor && anchor) {
    // Clone children and add data-track-action to the clickable element
    const enhancedChildren = React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement,
          {
            'data-track-action': trackingAction,
          } as any,
        )
      : children;

    return (
      <Anchor target={anchor} className={cn('inline-block', className)}>
        {enhancedChildren}
      </Anchor>
    );
  }

  if (isPopup && popup) {
    const isNewTheme = popup.theme === 'new';

    // Clone children and add data-track-action to the clickable element
    const enhancedChildren = React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement,
          {
            'data-track-action': trackingAction,
          } as any,
        )
      : children;

    return (
      <Dialog>
        <DialogTrigger asChild className="">
          <div className={className}>{enhancedChildren}</div>
        </DialogTrigger>
        <DialogContent
          className={cn(
            '!w-[min(170rem,calc(100%-4rem))] max-h-[80svh] bg-black overflow-y-auto lg:max-h-screen top-1/2 -translate-y-1/2 lg:p-0',
          )}
        >
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>

          <PopupContent
            content={popup.content}
            enableCountdown={popup?.enableCountdown}
            countdownTime={popup?.countdownTime}
            additionalContent={popup.additionalContent}
            theme={popup.theme}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return <div className={cn('inline-block', className)}>{children}</div>;
}
